// The beach ramp: a sloping skirt of sand that continues each beach edge's plateau down into the water, so the
// land "goes into the sea" instead of ending in a hex-edged step. Pure geometry (typed arrays, no THREE): one
// merged buffer for the whole coast, built once per terrain signature (renderer-crash-rca: no per-tile
// geometry, no per-publish rebuilds).
//
// Per beach edge: a grid of N+1 columns along the edge x ROWS rows across. Row 0 lies on the plateau (a hair
// above it), the last row ends below the water plane. The reach (how far the skirt runs into the sea) varies
// with a smooth world-space noise, so the waterline where the skirt meets the (wavy) water plane is an organic
// curve, not the hex edge. Corner columns are shared between neighbouring edges (same position, same reach, a
// mitred direction), so the coast is one continuous surface.

import { smoothField } from '../elevation.js';
import { PLATEAU_INSET, RIM_INSET, type ShoreEdge } from './shoreEdges.js';

export const RAMP_COLS = 8;
/** Row positions across the ramp, 0 = on the plateau, 1 = the submerged end (denser near the top). */
export const RAMP_ROWS: ReadonlyArray<number> = [0, 0.08, 0.18, 0.3, 0.45, 0.62, 0.8, 1];
export const RAMP_VERTS_PER_EDGE = (RAMP_COLS + 1) * RAMP_ROWS.length;
export const RAMP_TRIS_PER_EDGE = RAMP_COLS * (RAMP_ROWS.length - 1) * 2;

const MIN_REACH = 0.38;
const MAX_REACH = 0.9;
/** How far below the water plane the ramp ends (waves bob the plane by up to ~0.1 on deep water). */
function dive(waterY: number): number {
  return waterY > -0.15 ? 0.09 : 0.14;
}

/** Smooth world-space reach modulation, 0.78..1.28: bays and headlands along the coast. */
export function reachNoise(x: number, z: number): number {
  const n =
    0.6 * smoothField(x * 1.1 + 11, z * 1.1 - 7) + 0.4 * smoothField(x * 2.7 + 5, z * 2.7 - 3);
  return 0.78 + 0.5 * n;
}

/**
 * Ramp reach (outward from the hex edge) before noise, by water class only: neighbouring edges that face the
 * same sea share their corner column exactly (coast water ripples less, and is shallower, than open ocean).
 */
export function baseReach(waterY: number): number {
  return waterY > -0.15 ? 0.42 : 0.6;
}

/** Reach at a point: the base reach modulated by the world-space noise (0.33 .. 0.77). */
export function reachAt(waterY: number, x: number, z: number): number {
  return Math.min(MAX_REACH, Math.max(MIN_REACH * 0.85, baseReach(waterY) * reachNoise(x, z)));
}

/**
 * Ramp surface height at outward distance `o` from the hex edge (negative = onto the plateau). Smoothstep from
 * the plateau top to the submerged end, kept above the 45 degree wall of the land prism so the wall is hidden.
 */
export function rampHeight(top: number, waterY: number, o: number, reach: number): number {
  const yTop = top + 0.004;
  const yEnd = waterY - dive(waterY);
  const u = Math.min(1, Math.max(0, (o + PLATEAU_INSET) / (reach + PLATEAU_INSET)));
  const y = yTop + (yEnd - yTop) * (u * u * (3 - 2 * u));
  const wall = top - 0.03 - Math.max(0, o + RIM_INSET);
  return o > -RIM_INSET ? Math.max(y, wall + 0.012) : y;
}

/** Outward distance from the hex edge at which the ramp (reach `reach`) crosses the water plane. */
export function waterlineOffset(top: number, waterY: number, reach: number): number {
  let lo = -PLATEAU_INSET;
  for (let o = lo; o <= reach; o += 0.01) {
    if (rampHeight(top, waterY, o, reach) <= waterY) return Math.max(0, o);
    lo = o;
  }
  return Math.max(0, lo);
}

export interface RampLayout {
  /** xyz per vertex (board-local world space). */
  readonly positions: Float32Array;
  /** vec4 per vertex: (u 0..1 across, water plane Y, wave amplitude class, outward distance o). */
  readonly info: Float32Array;
  readonly indices: Uint32Array;
  /** One entry per beach edge: tile key, for the fog-of-war tone update. */
  readonly tileKeys: ReadonlyArray<string>;
  readonly edgeCount: number;
}

const cornerKey = (x: number, z: number) => `${Math.round(x * 1000)},${Math.round(z * 1000)}`;

interface Corner {
  sx: number;
  sz: number;
  beach: number;
  other: number;
}

/** Wave amplitude by water class (TerrainWater WAVE_AMP): coast tiles ripple less than open ocean. */
export function waveAmpFor(waterY: number): number {
  return waterY > -0.15 ? 0.02 : 0.04;
}

/** Build the merged ramp for the beach edges in `edges` (cliff edges get no ramp). */
export function buildRampLayout(edges: ReadonlyArray<ShoreEdge>): RampLayout {
  const beach = edges.filter((e) => e.kind === 'beach');
  // Shared corner directions: the summed outward normals of every incident shore edge.
  const corners = new Map<string, Corner>();
  const touch = (x: number, z: number, nx: number, nz: number, isBeach: boolean) => {
    const k = cornerKey(x, z);
    let c = corners.get(k);
    if (!c) {
      c = { sx: 0, sz: 0, beach: 0, other: 0 };
      corners.set(k, c);
    }
    if (isBeach) {
      c.sx += nx;
      c.sz += nz;
      c.beach++;
    } else c.other++;
  };
  for (const e of edges) {
    const isBeach = e.kind === 'beach';
    touch(e.ax, e.az, e.nx, e.nz, isBeach);
    touch(e.bx, e.bz, e.nx, e.nz, isBeach);
  }

  const nV = beach.length * RAMP_VERTS_PER_EDGE;
  const positions = new Float32Array(nV * 3);
  const info = new Float32Array(nV * 4);
  const indices = new Uint32Array(beach.length * RAMP_TRIS_PER_EDGE * 3);
  const tileKeys: string[] = [];
  let v = 0;
  let ii = 0;
  const rows = RAMP_ROWS.length;

  const cornerDir = (x: number, z: number, e: ShoreEdge): [number, number, number, number] => {
    const c = corners.get(cornerKey(x, z))!;
    let dx = c.sx;
    let dz = c.sz;
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) {
      dx = e.nx;
      dz = e.nz;
    } else {
      dx /= len;
      dz /= len;
    }
    const miter = Math.min(1.5, 1 / Math.max(0.55, dx * e.nx + dz * e.nz));
    // Rock next to the sand, or a coast that just ends: the ramp tapers off instead of stopping dead.
    const taper = c.other > 0 ? 0.3 : c.beach >= 2 ? 1 : 0.6;
    return [dx, dz, miter, taper];
  };

  for (const e of edges) {
    if (e.kind !== 'beach') continue;
    tileKeys.push(`${e.q},${e.r}`);
    const [adx, adz, am, at] = cornerDir(e.ax, e.az, e);
    const [bdx, bdz, bm, bt] = cornerDir(e.bx, e.bz, e);
    const base = v;
    const amp = waveAmpFor(e.waterY);
    for (let ci = 0; ci <= RAMP_COLS; ci++) {
      const s = ci / RAMP_COLS;
      const px = e.ax + (e.bx - e.ax) * s;
      const pz = e.az + (e.bz - e.az) * s;
      let dx = adx + (bdx - adx) * s;
      let dz = adz + (bdz - adz) * s;
      const dl = Math.hypot(dx, dz) || 1;
      dx /= dl;
      dz /= dl;
      const miter = am + (bm - am) * s;
      const taper = at + (bt - at) * s;
      const reach = reachAt(e.waterY, px, pz) * (0.25 + 0.75 * taper);
      const minReach = Math.min(reach, MIN_REACH * 0.5);
      const R = Math.max(reach, minReach);
      for (let ri = 0; ri < rows; ri++) {
        const t = RAMP_ROWS[ri]!;
        const o = -PLATEAU_INSET + t * (R + PLATEAU_INSET);
        const out = o > 0;
        const ox = out ? dx * o * miter : e.nx * o;
        const oz = out ? dz * o * miter : e.nz * o;
        // A tapered end still has to dive below the water: shape its height with its own (shorter) reach.
        const y = rampHeight(e.top, e.waterY, o, Math.max(R, 0.05));
        positions.set([px + ox, y, pz + oz], v * 3);
        info.set([t, e.waterY, amp, o], v * 4);
        v++;
      }
    }
    for (let ci = 0; ci < RAMP_COLS; ci++) {
      for (let ri = 0; ri < rows - 1; ri++) {
        const a = base + ci * rows + ri;
        const b = base + (ci + 1) * rows + ri;
        const c = a + 1;
        const d = b + 1;
        for (const tri of [
          [a, b, c],
          [b, d, c],
        ] as const) {
          // Wind every triangle so it faces up (+y).
          const [i0, i1, i2] = tri;
          const ax = positions[i1 * 3]! - positions[i0 * 3]!;
          const az = positions[i1 * 3 + 2]! - positions[i0 * 3 + 2]!;
          const bx = positions[i2 * 3]! - positions[i0 * 3]!;
          const bz = positions[i2 * 3 + 2]! - positions[i0 * 3 + 2]!;
          const upY = az * bx - ax * bz; // y component of (b - a) x (c - a)... sign fixed below
          if (upY >= 0) indices.set([i0, i1, i2], ii);
          else indices.set([i0, i2, i1], ii);
          ii += 3;
        }
      }
    }
  }
  return { positions, info, indices, tileKeys, edgeCount: beach.length };
}
